import { 
  View, 
  Text, 
  SafeAreaView, 
  ScrollView, 
  StyleSheet, 
  Image, 
  TouchableOpacity,
  Alert,
  Linking,
  Platform,
  ActivityIndicator,
  Dimensions,
  PermissionsAndroid,
  Share,
  Modal
} from 'react-native';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colors } from '../constants/colors';
import { firebaseService } from '../services/FirebaseService';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove, Timestamp } from 'firebase/firestore';
import { WebView } from 'react-native-webview';
import ImageView from 'react-native-image-viewing';
import { Ionicons } from '@expo/vector-icons';
import * as FileSystem from 'expo-file-system';
import * as MediaLibrary from 'expo-media-library';
import EmojiSelector from 'react-native-emoji-selector';
import ShimmerPlaceholder from '../components/ShimmerPlaceholder';
import { handleWebStorage } from '../utils/platform';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Announcement {
  id: string;
  content: string;
  createdAt: Timestamp;
  date: string;
  day: string;
  time: string;
  imgurl1?: string;
  imgurl2?: string;
  imgurl3?: string;
  imgurl4?: string;
  imgurl5?: string;
  imgurl6?: string;
  imgurl7?: string;
  imgurl8?: string;
  imgurl9?: string;
  imgurl10?: string;
  reactions?: { [key: string]: { emoji: string; count: number; users: string[] } };
}

interface GroupedAnnouncements {
  [key: string]: Announcement[];
}

interface AnnouncementContentProps {
  content: string;
  onLinkPress: (url: string) => void;
}

interface Reaction {
  emoji: string;
  count: number;
  users: string[];
}

const { width } = Dimensions.get('window');

// Function to identify and format links in content
const formatContentWithLinks = (content: string) => {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;
  
  // Replace URLs with anchor tags
  return content.replace(urlRegex, (url) => {
    return `<a href="${url}" style="color: #2196F3; text-decoration: underline;">${url}</a>`;
  });
};

const createHtmlContent = (content: string) => {
  // First format any URLs in the text content
  const formattedContent = content.replace(
    /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g,
    (url) => `<a href="${url}" style="color: #2196F3; text-decoration: underline;">${url}</a>`
  );

  return `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            font-size: 16px;
            color: #000;
            margin: 0;
            padding: 0;
            background: transparent;
            line-height: 1.5;
            overflow: visible;
          }
          div, p {
            margin: 0 0 8px 0;
            overflow: visible;
          }
          div:last-child, p:last-child {
            margin-bottom: 0;
          }
          a {
            color: #2196F3;
            text-decoration: underline;
            word-break: break-all;
          }
          img {
            max-width: 100%;
            height: auto;
            margin-top: 8px;
            display: block;
          }
          * {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>${formattedContent}</body>
    </html>
  `;
};

const extractUrlFromContent = (content: string): string | null => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = content.match(urlRegex);
  return matches ? matches[0] : null;
};

const LinkPreview = ({ url }: { url: string }) => {
  const [preview, setPreview] = useState<{
    title?: string;
    description?: string;
    image?: string;
  }>({});

  useEffect(() => {
    // In a real app, you would use a link preview service here
    // For now, we'll just show the URL
    setPreview({
      title: url,
      description: '',
      image: '',
    });
  }, [url]);

  return (
    <TouchableOpacity 
      style={styles.linkPreview}
      onPress={() => Linking.openURL(url)}
    >
      <View style={styles.linkPreviewContent}>
        {preview.image && (
          <Image 
            source={{ uri: preview.image }} 
            style={styles.linkPreviewImage} 
          />
        )}
        <Text style={styles.linkPreviewTitle} numberOfLines={2}>
          {preview.title}
        </Text>
        {preview.description && (
          <Text style={styles.linkPreviewDescription} numberOfLines={1}>
            {preview.description}
          </Text>
        )}
      </View>
    </TouchableOpacity>
  );
};

const formatTime = (timeString: string) => {
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const AnnouncementContent = ({ content, onLinkPress }: AnnouncementContentProps) => {
  if (Platform.OS === 'web') {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: createHtmlContent(content)
        }}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          fontSize: '16px',
          color: '#000',
          lineHeight: 1.5,
        }}
        onClick={(e) => {
          const target = e.target as HTMLAnchorElement;
          if (target.tagName === 'A') {
            e.preventDefault();
            onLinkPress(target.href);
          }
        }}
      />
    );
  }

  return (
    <WebView
      source={{ html: createHtmlContent(content) }}
      style={[styles.webView, { height: 'auto', minHeight: 50 }]}
      scrollEnabled={false}
      showsVerticalScrollIndicator={false}
      onShouldStartLoadWithRequest={(request) => {
        if (request.url !== 'about:blank') {
          onLinkPress(request.url);
          return false;
        }
        return true;
      }}
    />
  );
};

const getDateHeader = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  
  // For messages in the last 24 hours
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
  if (date >= twentyFourHoursAgo) {
    return 'Today';
  }
  
  // For messages between 24-48 hours ago
  const fortyEightHoursAgo = new Date(now.getTime() - (48 * 60 * 60 * 1000));
  if (date >= fortyEightHoursAgo) {
    return 'Yesterday';
  }

  // Check if it's within this week
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  if (date >= weekStart) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  // Check if it's this year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  }

  // For older dates
  return date.toLocaleDateString('en-US', { 
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const requestStoragePermission = async () => {
  if (Platform.OS === 'web') {
    return true; // Storage permissions are handled differently on web
  }
  
  // Only import PermissionsAndroid for non-web platforms
  if (Platform.OS === 'android') {
    const { PermissionsAndroid } = require('react-native');
    try {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
        {
          title: "Storage Permission",
          message: "App needs access to storage to save images",
          buttonNeutral: "Ask Me Later",
          buttonNegative: "Cancel",
          buttonPositive: "OK"
        }
      );
      return granted === PermissionsAndroid.RESULTS.GRANTED;
    } catch (err) {
      console.error(err);
      return false;
    }
  }
  
  return true; // For iOS and other platforms
};

const EMOJI_LIST = ['👍', '❤️', '😂', '😮', '🙏', '👀', '💀', '😭', '😍', '😋', '🔥', '🤣'];

const handleImageSave = async (imageUrl: string) => {
  if (Platform.OS === 'web') {
    await handleWebStorage(imageUrl);
    return;
  }

  try {
    // Request permissions
    const { status } = await MediaLibrary.requestPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Denied', 'Please grant media library permissions to save images');
      return;
    }

    // Show downloading message
    Alert.alert('Downloading...', 'Please wait while we save your image');

    // Download the image
    const timestamp = new Date().getTime();
    const fileUri = `${FileSystem.cacheDirectory}announcement_${timestamp}.jpg`;
    
    const downloadResult = await FileSystem.downloadAsync(imageUrl, fileUri);

    if (downloadResult.status === 200) {
      // Save to media library
      const asset = await MediaLibrary.createAssetAsync(downloadResult.uri);
      await MediaLibrary.createAlbumAsync('NUST Life', asset, false);
      
      // Show success message
      Alert.alert('Success', 'Image saved to gallery!');
    } else {
      Alert.alert('Error', 'Failed to download image');
    }
  } catch (error) {
    console.error('Error saving image:', error);
    Alert.alert('Error', 'Failed to save image. Please try again.');
  }
};

interface ImageViewerModalProps {
  visible: boolean;
  images: string[];
  currentIndex: number;
  onClose: () => void;
}

const ImageViewerModal: React.FC<ImageViewerModalProps> = ({ visible, images, currentIndex, onClose }) => {
  const [index, setIndex] = useState(currentIndex);

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent={true} onRequestClose={onClose}>
      <View style={styles.imageViewerContainer}>
        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <Ionicons name="close" size={24} color="white" />
        </TouchableOpacity>
        <Image
          source={{ uri: images[index] }}
          style={styles.fullScreenImage}
          resizeMode="contain"
        />
        <View style={styles.imageViewerFooter}>
          <Text style={styles.imageCounter}>{`${index + 1} / ${images.length}`}</Text>
          <TouchableOpacity style={styles.saveButton} onPress={() => handleImageSave(images[index])}>
            <Ionicons name="download" size={24} color="white" />
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

export default function AnnouncementsScreen() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [groupedAnnouncements, setGroupedAnnouncements] = useState<GroupedAnnouncements>({});
  const [loadingImages, setLoadingImages] = useState<{ [key: string]: boolean }>({});
  const webViewRefs = useRef<{ [key: string]: WebView }>({});
  const scrollViewRef = useRef<ScrollView>(null);
  const insets = useSafeAreaInsets();

  useEffect(() => {
    const db = firebaseService.getDb();
    const announcementsRef = collection(db, 'announcements');
    const q = query(announcementsRef, orderBy('createdAt', 'asc')); // Changed to 'asc' to show oldest first

    try {
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const announcementsList: Announcement[] = [];
        snapshot.forEach((doc) => {
          announcementsList.push({ id: doc.id, ...doc.data() } as Announcement);
        });
        setAnnouncements(announcementsList);
        
        // Group announcements by date
        const grouped = announcementsList.reduce((groups: GroupedAnnouncements, announcement) => {
          const date = announcement.date;
          if (!groups[date]) {
            groups[date] = [];
          }
          groups[date].push(announcement);
          return groups;
        }, {});
        
        setGroupedAnnouncements(grouped);
        setLoading(false);

        // Scroll to bottom after loading
        setTimeout(() => {
          scrollViewRef.current?.scrollToEnd({ animated: false });
        }, 100);
      }, (error) => {
        console.error('Error loading announcements:', error);
        setError(error.message);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error: any) {
      console.error('Error setting up announcements listener:', error);
      setError(error.message);
      setLoading(false);
    }
  }, []);

  // Scroll to bottom when new announcement is added
  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [groupedAnnouncements]);

  const handleLinkPress = async (url: string) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      console.error('Error opening URL:', error);
    }
  };

  const handleImagePress = (images: string[], index: number) => {
    setSelectedImages(images);
    setSelectedImageIndex(index);
    setIsImageViewVisible(true);
  };

  const handleImageLoad = (imageUrl: string) => {
    setLoadingImages(prev => ({
      ...prev,
      [imageUrl]: false
    }));
  };

  const renderImages = (announcement: Announcement) => {
    const images = [];
    for (let i = 1; i <= 10; i++) {
      const imgUrl = announcement[`imgurl${i}` as keyof Announcement] as string;
      if (imgUrl) {
        // Initialize loading state for this image if not already set
        if (loadingImages[imgUrl] === undefined) {
          setLoadingImages(prev => ({
            ...prev,
            [imgUrl]: true
          }));
        }
        images.push(imgUrl);
      }
    }
    
    if (images.length === 0) return null;

    // Calculate dimensions based on number of images
    let imageWidth: number;
    let imageHeight: number;
    const containerWidth = width - 64; // Account for announcement card padding

    if (images.length === 1) {
      imageWidth = containerWidth;
      imageHeight = containerWidth * 0.5; // Make it less tall
    } else if (images.length <= 3) {
      // For 2-3 images, display them in a single row
      imageWidth = (containerWidth - (images.length - 1) * 8) / images.length;
      imageHeight = containerWidth * 0.25; // Even shorter for multiple images
    } else {
      // For 4+ images, display in rows of 3
      imageWidth = (containerWidth - 16) / 3;
      imageHeight = containerWidth * 0.2; // Shortest for grid layout
    }

    return (
      <View style={[
        styles.imageGrid,
        images.length === 1 ? styles.singleImageGrid : 
        images.length <= 3 ? styles.rowImageGrid : styles.gridImageGrid
      ]}>
        {images.map((image, index) => (
          <TouchableOpacity
            key={index}
            onPress={() => handleImagePress(images, index)}
            onLongPress={() => handleImageSave(image)}
            style={[
              styles.imageContainer,
              {
                width: imageWidth,
                height: imageHeight,
                marginRight: (images.length <= 3 && index !== images.length - 1) ? 8 : 4,
                marginLeft: (images.length <= 3 && index === 0) ? 0 : 4,
              }
            ]}
          >
            {loadingImages[image] && (
              <ShimmerPlaceholder
                width={imageWidth}
                height={imageHeight}
                borderRadius={8}
              />
            )}
            <Image
              source={{ uri: image }}
              style={[
                styles.announcementImage,
                loadingImages[image] && styles.hiddenImage
              ]}
              resizeMode="cover"
              onLoad={() => handleImageLoad(image)}
            />
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  const handleReaction = async (announcementId: string, emoji: string) => {
    try {
      const db = firebaseService.getDb();
      const currentUser = firebaseService.getCurrentUser();
      
      if (!currentUser) {
        Alert.alert('Error', 'You must be logged in to react to announcements');
        return;
      }

      const userId = currentUser.uid;
      const announcementRef = doc(db, 'announcements', announcementId);
      const announcement = announcements.find(a => a.id === announcementId);
      
      // Initialize reactions if they don't exist
      if (!announcement?.reactions) {
        await updateDoc(announcementRef, {
          reactions: {
            [emoji]: {
              emoji,
              count: 1,
              users: [userId]
            }
          }
        });
        return;
      }

      // Check if user has any existing reaction
      let existingUserReaction: string | null = null;
      Object.entries(announcement.reactions).forEach(([key, reaction]) => {
        if (reaction.users.includes(userId)) {
          existingUserReaction = key;
        }
      });

      // If clicking the same emoji, remove the reaction
      if (existingUserReaction === emoji) {
        await updateDoc(announcementRef, {
          [`reactions.${emoji}.count`]: announcement.reactions[emoji].count - 1,
          [`reactions.${emoji}.users`]: arrayRemove(userId)
        });
        return;
      }

      // Batch update: remove old reaction and add new one
      if (existingUserReaction) {
        // Remove old reaction
        await updateDoc(announcementRef, {
          [`reactions.${existingUserReaction}.count`]: announcement.reactions[existingUserReaction].count - 1,
          [`reactions.${existingUserReaction}.users`]: arrayRemove(userId)
        });
      }

      // Add new reaction
      const existingReaction = announcement.reactions[emoji];
      await updateDoc(announcementRef, {
        [`reactions.${emoji}.emoji`]: emoji,
        [`reactions.${emoji}.count`]: (existingReaction?.count || 0) + 1,
        [`reactions.${emoji}.users`]: arrayUnion(userId)
      });

    } catch (error) {
      console.error('Error updating reaction:', error);
      Alert.alert('Error', 'Failed to update reaction');
    }
  };

  const renderReactions = (announcement: Announcement) => {
    return (
      <View style={styles.reactionsContainer}>
        {announcement.reactions && Object.values(announcement.reactions)
          .filter(reaction => reaction.count > 0)
          .map((reaction, index) => (
            <TouchableOpacity
              key={`${announcement.id}-${reaction.emoji}-${index}`}
              style={[
                styles.reactionBubble,
                reaction.users.includes(firebaseService.getCurrentUser()?.uid) && styles.reactionBubbleSelected
              ]}
              onPress={() => handleReaction(announcement.id, reaction.emoji)}
            >
              <Text style={styles.reactionEmoji}>{reaction.emoji}</Text>
              <Text style={styles.reactionCount}>{reaction.count}</Text>
            </TouchableOpacity>
          ))
        }
        <TouchableOpacity
          style={styles.addReactionButton}
          onPress={() => {
            setSelectedAnnouncementId(announcement.id);
            setShowEmojiPicker(true);
          }}
        >
          <Ionicons name="add-circle-outline" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>
    );
  };

  const renderAnnouncement = (announcement: Announcement) => {
    return (
      <View key={announcement.id} style={styles.announcementCard}>
        <View style={styles.messageContent}>
          <AnnouncementContent 
            content={announcement.content} 
            onLinkPress={handleLinkPress}
          />
          {renderImages(announcement)}
          {renderReactions(announcement)}
        </View>
        <Text style={styles.timeText}>{formatTime(announcement.time)}</Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView 
        ref={scrollViewRef}
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        scrollEventThrottle={16}
        bounces={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.greetingContainer}>
              <Text style={styles.greeting}>NUST Updates</Text>
              <View style={styles.titleContainer}>
                <Text style={styles.headerTitle}>My</Text>
                <Text style={[styles.headerTitle, { color: colors.primary }]}> Announcements</Text>
              </View>
            </View>
            <View style={styles.headerRight}>
            </View>
          </View>
          <View style={styles.headerDecoration} />
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <>
            {Object.keys(groupedAnnouncements)
              .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
              .map((date) => (
                <View key={date} style={styles.dateGroup}>
                  <View style={styles.dateHeader}>
                    <Text style={styles.dateText}>{getDateHeader(date)}</Text>
                  </View>
                  {groupedAnnouncements[date]
                    .sort((a, b) => {
                      const timeA = new Date(`${a.date} ${a.time}`).getTime();
                      const timeB = new Date(`${b.date} ${b.time}`).getTime();
                      return timeA - timeB;
                    })
                    .map((announcement) => (
                      renderAnnouncement(announcement)
                    ))}
                </View>
              ))}
          </>
        )}
      </ScrollView>

      <ImageViewerModal
        visible={isImageViewVisible}
        images={selectedImages}
        currentIndex={selectedImageIndex}
        onClose={() => setIsImageViewVisible(false)}
      />
      <Modal
        visible={showEmojiPicker}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowEmojiPicker(false)}
      >
        <View style={styles.emojiPickerContainer}>
          <View style={styles.emojiPickerHeader}>
            <Text style={styles.emojiPickerTitle}>Add Reaction</Text>
            <TouchableOpacity onPress={() => setShowEmojiPicker(false)}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>
          <View style={styles.quickEmojiContainer}>
            {EMOJI_LIST.map((emoji) => (
              <TouchableOpacity
                key={emoji}
                style={styles.quickEmojiButton}
                onPress={() => {
                  if (selectedAnnouncementId) {
                    handleReaction(selectedAnnouncementId, emoji);
                    setShowEmojiPicker(false);
                    setSelectedAnnouncementId(null);
                  }
                }}
              >
                <Text style={styles.quickEmojiText}>{emoji}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E5DDD5',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  scrollContent: {
    paddingBottom: Platform.select({
      ios: 90,
      android: 100,
      default: 90
    }), // Increased padding to account for bottom tab bar
  },
  header: {
    paddingTop: 20,
    paddingBottom: 15,
    backgroundColor: colors.surface,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    marginHorizontal: -20,
    paddingHorizontal: 20,
    position: 'relative',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  greetingContainer: {
    flex: 1,
  },
  greeting: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 4,
    fontWeight: '500',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: '800',
    color: colors.text,
    letterSpacing: -1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerDecoration: {
    position: 'absolute',
    bottom: 0,
    left: 30,
    right: 30,
    height: 6,
    backgroundColor: colors.primary,
    opacity: 0.15,
    borderRadius: 3,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dateGroup: {
    marginBottom: 24,
  },
  dateHeader: {
    alignItems: 'center',
    marginVertical: 16,
  },
  dateText: {
    fontSize: 13,
    color: colors.textSecondary,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    overflow: 'hidden',
    textAlign: 'center',
  },
  announcementCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    width: '100%',
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  messageContent: {
    width: '100%',
  },
  webView: {
    width: '100%',
    backgroundColor: 'transparent',
    opacity: 1,
  },
  imageGrid: {
    marginTop: 8,
    marginBottom: 8,
  },
  singleImageGrid: {
    alignItems: 'center',
  },
  rowImageGrid: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  gridImageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  imageContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    marginBottom: 8,
  },
  announcementImage: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  hiddenImage: {
    position: 'absolute',
    opacity: 0,
  },
  timeText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 8,
    textAlign: 'right',
  },
  imageViewerContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullScreenImage: {
    width: '100%',
    height: '80%',
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 1,
    padding: 10,
  },
  imageViewerFooter: {
    height: 64,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
  },
  imageCounter: {
    color: 'white',
    fontSize: 16,
  },
  saveButton: {
    padding: 8,
  },
  reactionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
  },
  reactionBubble: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  reactionBubbleSelected: {
    backgroundColor: `${colors.primary}20`,
    borderColor: colors.primary,
  },
  reactionEmoji: {
    fontSize: 16,
    marginRight: 4,
  },
  reactionCount: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  addReactionButton: {
    padding: 4,
  },
  emojiPickerContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 16,
    ...Platform.select({
      ios: {
        shadowColor: colors.text,
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 4,
      },
    }),
  },
  emojiPickerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  emojiPickerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
  },
  quickEmojiContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 16,
  },
  quickEmojiButton: {
    padding: 8,
  },
  quickEmojiText: {
    fontSize: 24,
  },
});
